import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaExclamationTriangle, FaCheckCircle, FaChevronDown, FaChevronRight, FaEye, FaSyncAlt } from 'react-icons/fa';
import { list, resolveDispute } from '../api/api';
import ResolveDisputeModal from '../components/ResolveDisputeModal';
import { TableRowsSkeleton } from '../components/LoadingState';

const StatusBadge = ({ status, outcome }) => {
  if (status === 'resolved') {
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${outcome === 'changed' ? 'bg-cloudza-50 text-cloudza-700 border border-cloudza-100' : 'bg-green-50 text-green-700 border border-green-200'}`}>
        <FaCheckCircle aria-hidden="true" />
        Resolved &mdash; {outcome === 'changed' ? 'Changed' : 'Upheld'}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
      <FaExclamationTriangle aria-hidden="true" />
      Pending
    </span>
  );
};

const DetailRow = ({ label, value }) => (
  <div>
    <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">{label}</p>
    <p className="text-sm text-gray-800 mt-0.5">{value || <span className="text-gray-400">&mdash;</span>}</p>
  </div>
);

const FILTERS = [
  { value: 'pending', label: 'Pending' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'all', label: 'All' },
];

// A call record is "disputed" if the indexer stamped any dispute field on it —
// used by the fallback path below when the disputed#<bool> facet query fails.
const isDisputedRecord = (call) =>
  call.disputed === true || call.disputed === 'true' || !!call.disputeStatus;

const Disputes = ({ setAlert }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [editingDispute, setEditingDispute] = useState(null);
  const [expandedKey, setExpandedKey] = useState(null);

  const loadDisputes = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await list({
        count: window.pcaSettings?.api?.pageSize || 25,
        disputed: 'true',
      });
      setData(response);
    } catch (err) {
      // The disputed#<bool> facet query needs backend support (list handler +
      // GSI facet items). Where the deployed API predates it, fall back to a
      // plain listing and pick the disputed calls out client-side, so the page
      // degrades to "possibly incomplete" instead of a dead error toast.
      console.warn('Disputed facet query failed, falling back to plain list:', err);
      try {
        const response = await list({ count: 500 });
        setData({
          ...response,
          Records: (response?.Records || []).filter(isDisputedRecord),
        });
      } catch (fallbackErr) {
        console.error('Failed to load disputes', fallbackErr);
        setError(fallbackErr);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDisputes();
  }, [loadDisputes]);

  useEffect(() => {
    if (error) {
      setAlert({ variant: 'error', heading: 'Error', text: 'Failed to load disputes.' });
    }
  }, [error, setAlert]);

  const disputes = React.useMemo(() => {
    if (!data?.Records) return [];
    if (statusFilter === 'pending') {
      return data.Records.filter(call => call.disputeStatus === 'pending');
    } else if (statusFilter === 'resolved') {
      return data.Records.filter(call => call.disputeStatus === 'resolved');
    }
    return data.Records;
  }, [data, statusFilter]);

  const counts = React.useMemo(() => {
    const records = data?.Records || [];
    return {
      pending: records.filter(call => call.disputeStatus === 'pending').length,
      resolved: records.filter(call => call.disputeStatus === 'resolved').length,
      all: records.length,
    };
  }, [data]);

  // The list page can only resolve as "upheld" (no change) - actually changing the
  // assessment requires the editable compliance/quality/conduct fields, which only exist on
  // the call's own drill-down. See "View Call" below for that path.
  const handleResolveDispute = async (reason) => {
    const call = editingDispute;
    const key = call.key || call.filename;
    await resolveDispute(key, 'upheld', reason);
    setData(prev => ({
      ...prev,
      Records: (prev?.Records || []).map(r =>
        (r.key || r.filename) === key
          ? { ...r, disputeStatus: 'resolved', disputeOutcome: 'upheld', disputeResolutionReason: reason }
          : r
      ),
    }));
    setEditingDispute(null);
    setAlert({
      variant: 'success',
      heading: 'Dispute Resolved',
      text: 'The dispute has been marked as reviewed.'
    });
  };

  const agentInitials = (name) => {
    if (!name) return '?';
    return name.trim().split(/[\s._@]+/).filter(Boolean).map((w) => w[0]).slice(0, 2).join('').toUpperCase();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-y-2">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Disputes</h1>
          <p className="text-gray-500 text-sm mt-1">
            Review assessments contested by call agents and record the outcome.
          </p>
        </div>
        <button
          type="button"
          className="btn-outline"
          onClick={loadDisputes}
          disabled={isLoading}
        >
          <FaSyncAlt className={`h-3 w-3 ${isLoading ? 'animate-spin' : ''}`} />
          {isLoading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      <div className="inline-flex h-[42px] items-center rounded-full border border-gray-200 p-0.5 bg-gray-50">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            onClick={() => setStatusFilter(f.value)}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${
              statusFilter === f.value
                ? 'bg-cloudza-600 text-white shadow-sm'
                : 'text-gray-500 hover:text-gray-800'
            }`}
            aria-pressed={statusFilter === f.value}
          >
            {f.label} ({counts[f.value]})
          </button>
        ))}
      </div>

      <div className="card !p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-[#fafafc]">
              <tr>
                <th className="px-4 py-3" />
                <th className="px-6 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Agent</th>
                <th className="px-6 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Dispute Reason</th>
                <th className="px-6 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Disputed At</th>
                <th className="px-6 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3" />
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {isLoading ? (
                <TableRowsSkeleton columns={6} rows={4} />
              ) : disputes.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-14 text-center">
                    <FaCheckCircle className="mx-auto mb-3 h-8 w-8 text-gray-300" aria-hidden="true" />
                    <p className="text-sm font-medium text-gray-600">
                      {statusFilter === 'pending'
                        ? 'No pending disputes — nothing waiting on review.'
                        : statusFilter === 'resolved'
                        ? 'No resolved disputes yet.'
                        : 'No disputes found.'}
                    </p>
                  </td>
                </tr>
              ) : disputes.map((call) => {
                const key = call.key || call.filename;
                const isExpanded = expandedKey === key;
                const agent = call.agent || call.disputedBy;
                return (
                  <React.Fragment key={key}>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-4 whitespace-nowrap align-middle">
                        <button
                          onClick={() => setExpandedKey(isExpanded ? null : key)}
                          className="text-gray-400 hover:text-gray-700 p-1"
                          aria-label={isExpanded ? 'Collapse' : 'Expand'}
                          aria-expanded={isExpanded}
                        >
                          {isExpanded ? <FaChevronDown className="h-3 w-3" /> : <FaChevronRight className="h-3 w-3" />}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-9 h-9 flex-shrink-0 rounded-xl bg-gradient-to-br from-cloudza-500 to-cloudza-700 text-white text-xs font-bold flex items-center justify-center uppercase shadow-sm">
                            {agentInitials(agent)}
                          </div>
                          <span className="font-medium text-gray-800 truncate">{agent || 'Unknown'}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 max-w-sm">
                        <p className="text-gray-700 line-clamp-2">{call.disputeReason}</p>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                        {call.disputedAt ? new Date(call.disputedAt).toLocaleString() : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <StatusBadge status={call.disputeStatus} outcome={call.disputeOutcome} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            className="btn-outline text-sm"
                            onClick={() => navigate(`/dashboard/${key}`)}
                          >
                            <FaEye className="h-3 w-3" /> View Call
                          </button>
                          {call.disputeStatus !== 'resolved' && (
                            <button
                              onClick={() => setEditingDispute(call)}
                              className="btn-primary text-sm"
                              title="Records the dispute as reviewed with no change to the assessment. To change it, open the call first."
                            >
                              No Change Needed
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="bg-gray-50">
                        <td />
                        <td colSpan="5" className="px-6 py-4">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl">
                            <DetailRow label="Dispute Reason" value={call.disputeReason} />
                            <DetailRow label="Disputed By" value={call.disputedBy} />
                            <DetailRow
                              label="Disputed At"
                              value={call.disputedAt ? new Date(call.disputedAt).toLocaleString() : null}
                            />
                            {call.disputeStatus === 'resolved' && (
                              <>
                                <DetailRow
                                  label="Outcome"
                                  value={call.disputeOutcome === 'changed' ? 'Assessment Changed' : 'Assessment Upheld'}
                                />
                                <DetailRow label="Resolution Reason" value={call.disputeResolutionReason} />
                                <DetailRow label="Resolved By" value={call.disputeResolvedBy} />
                                <DetailRow
                                  label="Resolved At"
                                  value={call.disputeResolvedAt ? new Date(call.disputeResolvedAt).toLocaleString() : null}
                                />
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <ResolveDisputeModal
        open={!!editingDispute}
        onClose={() => setEditingDispute(null)}
        onSave={handleResolveDispute}
        outcome="upheld"
        disputeReason={editingDispute?.disputeReason}
      />
    </div>
  );
};

export default Disputes;
